import {
  DECISION_PRIORITIES,
} from "../models";


const EMPTY_DECISIONS = [];


export function decisionPrioritizationEngine(
  decisions = EMPTY_DECISIONS
) {

  if (!Array.isArray(decisions)) {
    return [];
  }


  const evaluatedDecisions =
    decisions.map(
      (decision) => {

        const priority =
          evaluateDecisionPriority(
            decision
          );


        return {
          ...decision,

          priority: {
            ...priority,

            /*
             * Rank is assigned after
             * all decisions are scored.
             */
            rank: null,
          },
        };

      }
    );


  const sortedDecisions =
    [...evaluatedDecisions]
      .sort(compareDecisionPriority);


  return sortedDecisions.map(
    (decision, index) => ({
      ...decision,

      priority: {
        ...decision.priority,
        rank: index + 1,
      },
    })
  );
}


function evaluateDecisionPriority(
  decision
) {

  switch (decision?.engine) {

    case "risk_financing_engine":
      return evaluateRiskFinancingDecision(
        decision
      );

    default:
      return createDefaultPriority();
  }
}


function evaluateRiskFinancingDecision(
  decision
) {

  const impact =
    decision?.financialImpact || {};

  const alternatives =
    Array.isArray(
      decision?.evidence?.alternatives
    )
      ? decision.evidence.alternatives
      : [];


  const selectedStrategy =
    normalizeText(
      decision?.strategy
    );


  const selectedAlternative =
    alternatives.find(
      (alternative) =>
        alternative?.selected === true ||
        normalizeText(
          alternative?.strategy
        ) === selectedStrategy
    ) ||
    null;


  const currentAlternative =
    alternatives.find(
      (alternative) =>
        normalizeText(
          alternative?.strategy
        ) === "current policy"
    ) ||
    null;


  const selectedCost =
    resolveNumber(
      impact.totalCost,
      selectedAlternative?.totalCost
    );


  const currentCost =
    resolveNumber(
      currentAlternative?.totalCost
    );


  const expectedSavings =
    selectedCost !== null &&
    currentCost !== null
      ? Math.max(
          currentCost -
          selectedCost,
          0
        )
      : 0;


  const savingsRatio =
    currentCost > 0
      ? expectedSavings /
        currentCost
      : 0;


  const transferredLoss =
    resolveNumber(
      impact.transferredLoss,
      selectedAlternative
        ?.transferredLoss
    ) || 0;


  const retainedLoss =
    resolveNumber(
      impact.retainedLoss,
      selectedAlternative
        ?.retainedLoss
    ) || 0;


  const tailExposure =
    resolveNumber(
      impact.tailExposure,
      selectedAlternative
        ?.tailExposure
    ) || 0;


  const totalExposure =
    transferredLoss +
    retainedLoss;


  const tailExposureRatio =
    totalExposure > 0
      ? tailExposure /
        totalExposure
      : 0;


  /*
   * Priority score:
   *
   * 70% expected financial opportunity
   * 30% unresolved tail exposure
   */
  const score =
    Math.round(
      clamp(
        (
          savingsRatio * 0.7 +
          tailExposureRatio * 0.3
        ) * 100,
        0,
        100
      )
    );


  return {
    score,

    level:
      resolvePriorityLevel(
        score
      ),

    rationale: {
      code:
        "FINANCIAL_OPPORTUNITY_AND_TAIL_EXPOSURE",

      description:
        expectedSavings > 0
          ? "Priority reflects expected cost savings versus the current policy and the remaining tail exposure."
          : "Priority reflects the remaining tail exposure because no measurable cost saving was identified.",

      expectedSavings,

      savingsRatio,

      tailExposureRatio,
    },
  };
}


function createDefaultPriority() {

  return {
    score: 0,

    level:
      DECISION_PRIORITIES.LOW,

    rationale: {
      code:
        "PRIORITY_NOT_EVALUATED",

      description:
        "No prioritization rule is currently available for this decision engine.",
    },
  };
}


function resolvePriorityLevel(
  score
) {

  if (score >= 60) {
    return DECISION_PRIORITIES.CRITICAL;
  }

  if (score >= 35) {
    return DECISION_PRIORITIES.HIGH;
  }

  if (score >= 15) {
    return DECISION_PRIORITIES.MEDIUM;
  }

  return DECISION_PRIORITIES.LOW;
}


function compareDecisionPriority(
  left,
  right
) {

  const leftScore =
    resolveNumber(
      left?.priority?.score
    ) || 0;

  const rightScore =
    resolveNumber(
      right?.priority?.score
    ) || 0;


  if (rightScore !== leftScore) {
    return rightScore - leftScore;
  }


  return normalizeText(
    left?.title
  ).localeCompare(
    normalizeText(
      right?.title
    )
  );
}


function normalizeText(value) {

  return String(value || "")
    .trim()
    .toLowerCase();
}


function resolveNumber(...values) {

  for (const value of values) {

    if (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      Number.isFinite(
        Number(value)
      )
    ) {
      return Number(value);
    }

  }

  return null;
}


function clamp(
  value,
  minimum,
  maximum
) {

  return Math.min(
    Math.max(
      value,
      minimum
    ),
    maximum
  );
}


export default decisionPrioritizationEngine;